import React, { useRef, useState } from "react";
import {
  Button,
  Alert,
  TextInput,
  FileButton,
  Group,
  Text,
} from "@mantine/core";
import { IconCheck, IconAlertCircle, IconUpload } from "@tabler/icons-react";
import { getAdminApiEndpoint, getApiEndpoint } from "../../config/config";
import { adminHeaders } from "../../services/adminApi";
import { useAuth } from "../../context/AuthContext";
import PageContent from "../shared/PageContent/PageContent";

const AgoraChatPage = () => {
  const { getVerifiedJWT } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const [channelUrl, setChannelUrl] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const resetFileRef = useRef(null);

  const handleBackfillUsers = async () => {
    setLoading(true);
    setSuccess(null);
    setError(null);

    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getAdminApiEndpoint("/v2/admin/backfill-agora-users"),
        {
          method: "GET",
          headers: adminHeaders(jwt),
        }
      );

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const result = await response.json();
      setSuccess(result.message || "Backfill completed successfully.");
    } catch (err) {
      setError(err.message || "An error occurred during backfill.");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadVideo = async () => {
    setUploadSuccess(null);
    setUploadError(null);

    if (!channelUrl.trim()) {
      setUploadError("Channel URL is required.");
      return;
    }
    if (!videoFile) {
      setUploadError("Please select a video file.");
      return;
    }

    setUploading(true);
    try {
      const jwt = await getVerifiedJWT();
      const contentType = videoFile.type || "video/mp4";

      const apiResponse = await fetch(getApiEndpoint("/v1/chat/video/upload"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          channel_url: channelUrl.trim(),
          video_file_name: videoFile.name,
          video_file_content_type: contentType,
        }),
      });

      if (!apiResponse.ok) {
        const errorData = await apiResponse.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to get upload URL");
      }

      const result = await apiResponse.json();
      const responseData = result.data || result;
      const presignedUrl =
        responseData.presign?.uploadUrl || responseData.uploadUrl;

      if (!presignedUrl) {
        throw new Error("No presigned URL received from server");
      }

      const uploadResponse = await fetch(presignedUrl, {
        method: "PUT",
        body: videoFile,
        headers: {
          "Content-Type": contentType,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status}`);
      }

      setUploadSuccess("Video uploaded successfully.");
      setVideoFile(null);
      resetFileRef.current?.();
    } catch (err) {
      setUploadError(err.message || "An error occurred during upload.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <PageContent title="Agora Chat">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          maxWidth: 480,
        }}
      >
        <Button
          onClick={handleBackfillUsers}
          loading={loading}
          leftSection={<IconCheck size={16} />}
        >
          Backfill Users
        </Button>

        {success && (
          <Alert icon={<IconCheck size={16} />} color="green" title="Success">
            {success}
          </Alert>
        )}

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
            {error}
          </Alert>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
            marginTop: "1.5rem",
            paddingTop: "1.5rem",
            borderTop: "1px solid var(--mantine-color-gray-3)",
          }}
        >
          <Text fw={600}>Upload Video to Channel</Text>
          <TextInput
            label="Channel URL"
            placeholder="https://chat.example.com/channels/general"
            value={channelUrl}
            onChange={event => setChannelUrl(event.currentTarget.value)}
            required
          />
          <Group>
            <FileButton
              resetRef={resetFileRef}
              onChange={setVideoFile}
              accept="video/*"
            >
              {props => (
                <Button
                  {...props}
                  variant="default"
                  leftSection={<IconUpload size={16} />}
                >
                  Select video
                </Button>
              )}
            </FileButton>
            {videoFile && (
              <Text size="sm" c="dimmed">
                {videoFile.name}
              </Text>
            )}
          </Group>
          <Button
            onClick={handleUploadVideo}
            loading={uploading}
            leftSection={<IconUpload size={16} />}
            disabled={!channelUrl.trim() || !videoFile}
          >
            Upload Video
          </Button>

          {uploadSuccess && (
            <Alert icon={<IconCheck size={16} />} color="green" title="Success">
              {uploadSuccess}
            </Alert>
          )}

          {uploadError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              title="Error"
            >
              {uploadError}
            </Alert>
          )}
        </div>
      </div>
    </PageContent>
  );
};

export default AgoraChatPage;
